import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../../components/Icon";
import { healthOf, isSectionUnavailable } from "../health";
import {
  Callout,
  Drawer,
  Empty,
  Field,
  Section,
  Switch,
  errText,
  useToast,
} from "../ui";
import {
  api,
  type AdminClientDetail,
  type AdminClientForm,
  type AdminUsersPage,
  type ApprovalChainStep,
  type ApprovalMatrixResponse,
  type ApprovalRuleRow,
  type ApproverDesignation,
  type MatrixPreviewResponse,
} from "../../services/api";

/* ===================================================================
   Forms & approvals.

   Two independent things on one page: which forms this customer can see
   (a per-form toggle that saves on the spot) and who approves what
   (the approval matrix, edited in a drawer).

   Rules are shown as they read at runtime: a numbered condition, the
   chain as arrow-joined steps, and the fallback that stops anything
   getting stuck. The predicate itself stays simple on purpose — a form
   whitelist plus an optional amount threshold covers what the engine is
   actually asked to do, and anything richer belongs in a rule authored
   with the full predicate tree.
   =================================================================== */

const APPROVER_KINDS: { value: string; label: string }[] = [
  { value: "direct_manager", label: "Requester's manager" },
  { value: "role:client_admin", label: "Anyone with the Client admin role" },
  { value: "role:approver", label: "Anyone with the Approver role" },
  { value: "role:service_desk", label: "iQuest service desk" },
];

function designationToKey(d: ApproverDesignation): string {
  if (d.designation === "direct_manager") return "direct_manager";
  if (d.designation === "user") return `user:${d.value ?? ""}`;
  return `${d.designation}:${d.value ?? ""}`;
}

function keyToDesignation(key: string): ApproverDesignation {
  if (key === "direct_manager") return { designation: "direct_manager", value: null };
  const [kind, ...rest] = key.split(":");
  const value = rest.join(":");
  if (kind === "user") return { designation: "user", value };
  if (kind === "tag") return { designation: "tag", value };
  return { designation: "role", value };
}

function describeApprover(d: ApproverDesignation, userName: (id: string) => string): string {
  if (d.designation === "direct_manager") return "Requester's manager";
  if (d.designation === "user") return userName(d.value ?? "");
  if (d.designation === "tag") return `Anyone tagged “${d.value}”`;
  const preset = APPROVER_KINDS.find((k) => k.value === `role:${d.value}`);
  return preset?.label ?? `Anyone with the ${d.value} role`;
}

/** The amount threshold, when a rule uses the simple shape this UI writes. */
function amountOf(condition: Record<string, unknown>): number | null {
  if (
    condition &&
    condition.field === "request_value" &&
    condition.op === "gt" &&
    typeof condition.value === "number"
  ) {
    return condition.value;
  }
  return null;
}

/**
 * True when the whole rule is something this drawer can round-trip without
 * losing anything: a predicate it knows how to write, and one approver per
 * step (the drawer's chain editor is one select per step). Anything richer is
 * shown read-only rather than silently flattened on save.
 */
function isEditableRule(rule: ApprovalRuleRow): boolean {
  const condition = rule.condition_jsonb;
  const simpleCondition =
    !condition ||
    Object.keys(condition).length === 0 ||
    amountOf(condition) !== null;
  const simpleChain =
    rule.action === "chain" &&
    rule.chain_steps_jsonb.length > 0 &&
    rule.chain_steps_jsonb.every((s) => s.approvers.length === 1);
  return simpleCondition && simpleChain;
}

export function FormsSection({
  client,
  onChanged,
}: {
  client: AdminClientDetail;
  onChanged: () => void;
}) {
  const health = healthOf(client);
  const toast = useToast();

  const { data: forms, mutate: mutateForms } = useSWR<AdminClientForm[]>(
    ["/api/admin/clients", client.id, "forms"],
    () => api.admin.clients.listForms(client.id),
  );
  const { data: matrix, mutate: mutateMatrix } = useSWR<ApprovalMatrixResponse>(
    ["/api/admin/matrix", client.id],
    () => api.admin.matrix.get(client.id),
  );
  const { data: users } = useSWR<AdminUsersPage>(
    ["/api/admin/users", client.id, "approvers"],
    () => api.admin.users.list({ company_id: client.id, page: 1, page_size: 200 }),
  );

  const [busyForm, setBusyForm] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<ApprovalRuleRow | "new" | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const userName = useMemo(() => {
    const byId = new Map((users?.items ?? []).map((u) => [u.id, u.display_name]));
    return (id: string) => byId.get(id) ?? "a specific person";
  }, [users]);

  const published = (forms ?? []).filter((f) => f.is_published);
  const drafts = (forms ?? []).filter((f) => !f.is_published);
  const rules = matrix?.rules ?? [];

  async function toggleForm(form: AdminClientForm, enabled: boolean) {
    setBusyForm(form.form_id);
    setFormError(null);
    try {
      await api.admin.clients.setFormConfig(client.id, form.form_id, { enabled });
      await mutateForms();
      onChanged();
      toast(enabled ? `${form.title} switched on` : `${form.title} switched off`);
    } catch (e) {
      setFormError(errText(e, "Could not change that form."));
    } finally {
      setBusyForm(null);
    }
  }

  async function removeRule(rule: ApprovalRuleRow) {
    if (!window.confirm(`Remove the rule “${rule.name}”?`)) return;
    try {
      await api.admin.matrix.deleteRule(client.id, rule.id);
      await mutateMatrix();
      onChanged();
      toast("Rule removed");
    } catch (e) {
      toast(errText(e, "Could not remove the rule"), "warn");
    }
  }

  return (
    <>
      <Section
        title="Forms available"
        desc={`What ${client.name} sees in their Service Hub. Global forms are on by default; switch off anything that doesn't apply. Each toggle saves on its own.`}
        error={formError}
        actions={
          <Link className="btn btn-ghost btn-sm" to="/admin/forms">
            <Icon name="doc" size={15} /> Form builder
          </Link>
        }
      >
        {(forms ?? []).length === 0 ? (
          <Empty
            title="No forms in scope"
            blurb="Global forms reach every customer; scope one to this customer from the catalogue."
          />
        ) : (
          <>
            {published.map((f) => (
              <FormRow
                key={f.form_id}
                form={f}
                busy={busyForm === f.form_id}
                onToggle={(v) => void toggleForm(f, v)}
              />
            ))}
            {drafts.length > 0 && (
              <>
                <div className="ad-eyebrow" style={{ margin: "18px 0 4px" }}>
                  Drafts — not visible to anyone yet
                </div>
                {drafts.map((f) => (
                  <FormRow
                    key={f.form_id}
                    form={f}
                    busy={busyForm === f.form_id}
                    onToggle={(v) => void toggleForm(f, v)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Section>

      <Section
        title="Approval routing"
        desc="Rules are checked top to bottom; the first match decides who approves. Changes here save as you make them."
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setEditingRule("new")}
          >
            <Icon name="plus" size={15} /> Add rule
          </button>
        }
      >
        {client.setup_health &&
          !isSectionUnavailable(health, "forms") &&
          health.forms === "warn" && (
          <div style={{ marginBottom: 14 }}>
            <Callout kind="warn">
              Forms are enabled but there is no routing rule. Approval requests will fall
              back to the iQuest service desk.
            </Callout>
          </div>
        )}

        {rules.length === 0 ? (
          <Empty
            title="No routing rules"
            blurb="Add one so approval requests reach the right person."
          />
        ) : (
          rules.map((rule, i) => (
            <div className="ad-rule" key={rule.id}>
              <div>
                <div className="w">
                  {i + 1}. {rule.name}
                </div>
                <div className="chain">
                  {rule.action === "bypass" ? (
                    <span className="st">Auto-approved, no approver</span>
                  ) : (
                    rule.chain_steps_jsonb.map((step, j) => (
                      <span key={`${rule.id}-${j}`} style={{ display: "contents" }}>
                        {j > 0 && <Icon name="arrowRight" size={14} />}
                        <span className="st">
                          {step.approvers
                            .map((a) => describeApprover(a, userName))
                            .join(step.type === "parallel_all" ? " + " : " / ")}
                        </span>
                      </span>
                    ))
                  )}
                </div>
                <div className="fb">
                  {rule.form_scope_jsonb?.length
                    ? `Only for: ${rule.form_scope_jsonb.join(", ")}`
                    : "Applies to every form"}
                  {amountOf(rule.condition_jsonb) !== null &&
                    ` · only over $${amountOf(rule.condition_jsonb)?.toLocaleString()}`}
                  {!rule.is_active && " · inactive"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditingRule(rule)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => void removeRule(rule)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))
        )}

        <div className="ad-note" style={{ marginTop: 8 }}>
          Fallback if nobody in a chain can approve:{" "}
          <strong>{matrix?.fallback_approver_name ?? "the iQuest service desk"}</strong>.
          Test it:{" "}
          <button
            type="button"
            className="ad-link"
            style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }}
            onClick={() => setPreviewing(true)}
          >
            preview who would approve a sample request
          </button>
          .
        </div>
      </Section>

      {editingRule && (
        <RuleDrawer
          client={client}
          rule={editingRule === "new" ? null : editingRule}
          forms={published}
          matrix={matrix}
          onClose={() => setEditingRule(null)}
          onSaved={async () => {
            setEditingRule(null);
            await mutateMatrix();
            onChanged();
          }}
        />
      )}

      {previewing && (
        <PreviewDrawer
          client={client}
          forms={published.filter((f) => f.enabled)}
          userName={userName}
          onClose={() => setPreviewing(false)}
        />
      )}
    </>
  );
}

function FormRow({
  form,
  busy,
  onToggle,
}: {
  form: AdminClientForm;
  busy: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <div className={`ad-access${form.enabled ? "" : " off"}`}>
      <span className="ico">
        <Icon name="doc" />
      </span>
      <span className="bd">
        <span className="t">
          {form.title}
          {form.scope !== "global" && (
            <span className="ad-scope" style={{ marginLeft: 6 }}>
              {form.scope === "single_company" ? "Only this customer" : "Selected customers"}
            </span>
          )}
        </span>
        <span className="s">
          {form.category ?? "Uncategorised"} · {form.is_published ? "published" : "draft"}
        </span>
      </span>
      <Switch
        checked={form.enabled}
        disabled={busy}
        label={`Enable ${form.title}`}
        onChange={onToggle}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Rule drawer                                                        */
/* ------------------------------------------------------------------ */

function RuleDrawer({
  client,
  rule,
  forms,
  matrix,
  onClose,
  onSaved,
}: {
  client: AdminClientDetail;
  rule: ApprovalRuleRow | null;
  forms: AdminClientForm[];
  matrix: ApprovalMatrixResponse | undefined;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const isNew = rule === null;
  const editable = isNew || isEditableRule(rule);

  const [name, setName] = useState(rule?.name ?? "");
  const [formSlug, setFormSlug] = useState(rule?.form_scope_jsonb?.[0] ?? "");
  const [amount, setAmount] = useState(
    rule ? (amountOf(rule.condition_jsonb)?.toString() ?? "") : "",
  );
  const [steps, setSteps] = useState<string[]>(
    rule?.chain_steps_jsonb.length
      ? rule.chain_steps_jsonb.map((s) => designationToKey(s.approvers[0] ?? { designation: "direct_manager" }))
      : ["direct_manager"],
  );
  const [fallback, setFallback] = useState(matrix?.fallback_approver_user_id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: users } = useSWR<AdminUsersPage>(
    ["/api/admin/users", client.id, "rule-drawer"],
    () => api.admin.users.list({ company_id: client.id, page: 1, page_size: 200 }),
  );
  const activeUsers = (users?.items ?? []).filter((u) => u.status === "active");

  const approverOptions = [
    ...APPROVER_KINDS,
    ...activeUsers.map((u) => ({ value: `user:${u.id}`, label: u.display_name })),
  ];

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give the rule a name — it's what the routing list reads back.");
      return;
    }
    const parsedAmount = amount.trim() ? Number(amount) : null;
    if (parsedAmount !== null && !Number.isFinite(parsedAmount)) {
      setError("The amount threshold must be a number.");
      return;
    }
    setBusy(true);
    setError(null);
    const chain: ApprovalChainStep[] = steps.map((key, i) => ({
      name: `Step ${i + 1}`,
      type: "sequential",
      approvers: [keyToDesignation(key)],
    }));
    const body = {
      name: trimmed,
      condition_jsonb:
        parsedAmount !== null
          ? { field: "request_value", op: "gt", value: parsedAmount }
          : {},
      action: "chain" as const,
      chain_steps_jsonb: chain,
      form_scope_jsonb: formSlug ? [formSlug] : null,
      is_active: rule?.is_active ?? true,
    };
    try {
      if (isNew) {
        await api.admin.matrix.createRule(client.id, {
          priority: (matrix?.rules.length ?? 0) * 10 + 100,
          ...body,
        });
      } else {
        await api.admin.matrix.updateRule(client.id, rule.id, body);
      }
      // The fallback lives on the matrix, not the rule, but it is the thing
      // that stops a chain dead-ending — so it is edited right here.
      if (fallback !== (matrix?.fallback_approver_user_id ?? "")) {
        await api.admin.matrix.setFallback(client.id, {
          fallback_approver_user_id: fallback || null,
        });
      }
      await onSaved();
      toast(isNew ? "Rule added" : "Rule saved");
    } catch (e) {
      setError(errText(e, "Could not save the rule."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow={client.name}
      title={isNew ? "Add routing rule" : "Edit rule"}
      desc="When a request matches, it walks the approval chain in order."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={busy || !editable}
          >
            {busy ? "Saving…" : isNew ? "Add rule" : "Save rule"}
          </button>
        </>
      }
    >
      {!editable && (
        <div style={{ marginBottom: 14 }}>
          <Callout kind="warn">
            This rule is richer than this editor writes — a condition it doesn&apos;t
            know, or a step with more than one approver — so it is shown read-only
            rather than silently flattened on save. Remove and re-add it to manage it
            here.
          </Callout>
        </div>
      )}

      <div className="ad-grid">
        <Field
          label="When"
          span
          note="Plain English, shown in the routing list. The form and threshold below are what actually match."
        >
          <input
            value={name}
            maxLength={200}
            placeholder="e.g. Hardware request over $2,000"
            disabled={!editable}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Form">
          <select
            value={formSlug}
            disabled={!editable}
            onChange={(e) => setFormSlug(e.target.value)}
          >
            <option value="">Any form</option>
            {forms.map((f) => (
              <option key={f.form_id} value={f.slug}>
                {f.title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Only when amount is over" note="Leave blank for no limit.">
          <input
            type="number"
            value={amount}
            placeholder="No limit"
            disabled={!editable}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
      </div>

      <div className="ad-eyebrow" style={{ margin: "20px 0 10px" }}>
        Approval chain
      </div>
      <div className="ad-grid">
        {steps.map((step, i) => (
          <Field key={i} label={`Step ${i + 1}`}>
            <div style={{ display: "flex", gap: 6 }}>
              <select
                value={step}
                disabled={!editable}
                onChange={(e) =>
                  setSteps((prev) => prev.map((s, j) => (j === i ? e.target.value : s)))
                }
              >
                {approverOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {steps.length > 1 && editable && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label={`Remove step ${i + 1}`}
                  onClick={() => setSteps((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Icon name="x" size={14} />
                </button>
              )}
            </div>
          </Field>
        ))}
      </div>
      {editable && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ marginTop: 10 }}
          onClick={() => setSteps((prev) => [...prev, "role:client_admin"])}
        >
          <Icon name="plus" size={14} /> Add a step
        </button>
      )}

      <div className="ad-grid" style={{ marginTop: 20 }}>
        <Field
          label="Fallback approver"
          span
          note="Used when a step has nobody to approve, so nothing gets stuck. Shared by every rule for this customer."
        >
          <select
            value={fallback}
            disabled={!editable}
            onChange={(e) => setFallback(e.target.value)}
          >
            <option value="">iQuest service desk</option>
            {activeUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/*  Routing preview                                                    */
/* ------------------------------------------------------------------ */

function PreviewDrawer({
  client,
  forms,
  userName,
  onClose,
}: {
  client: AdminClientDetail;
  forms: AdminClientForm[];
  userName: (id: string) => string;
  onClose: () => void;
}) {
  const [formSlug, setFormSlug] = useState(forms[0]?.slug ?? "");
  const [amount, setAmount] = useState("2500");
  const [result, setResult] = useState<MatrixPreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await api.admin.matrix.preview(client.id, {
          form_type_slug: formSlug || null,
          form_data: {},
          request_value: amount.trim() ? Number(amount) : null,
        }),
      );
    } catch (e) {
      setError(errText(e, "Preview failed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow={client.name}
      title="Preview approval routing"
      desc="Pick a sample request and see who would be asked to approve."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void run()}
            disabled={busy}
          >
            {busy ? "Running…" : "Run preview"}
          </button>
        </>
      }
    >
      <div className="ad-grid">
        <Field label="Form" span>
          <select value={formSlug} onChange={(e) => setFormSlug(e.target.value)}>
            <option value="">Any form</option>
            {forms.map((f) => (
              <option key={f.form_id} value={f.slug}>
                {f.title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
      </div>

      <div style={{ marginTop: 20 }}>
        {error && <div className="form-error">{error}</div>}
        {result &&
          (result.matched_rule_name ? (
            <Callout kind="ok">
              <strong>Matched: {result.matched_rule_name}</strong>
              <br />
              {result.action === "bypass"
                ? "Auto-approved — no approver is asked."
                : result.chain_steps_jsonb
                    .map(
                      (s, i) =>
                        `${i + 1}. ${s.approvers
                          .map((a) => describeApprover(a, userName))
                          .join(" / ")}`,
                    )
                    .join(" → ")}
            </Callout>
          ) : (
            <Callout kind="warn">
              No rule matches — this would go to{" "}
              {result.fallback_used ? "the fallback approver" : "the iQuest service desk"}.
            </Callout>
          ))}
      </div>
    </Drawer>
  );
}
